import {
  Component,
  OnInit,
  signal,
  inject,
  OnDestroy,
  computed,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil, debounceTime, switchMap, map, of, Observable } from 'rxjs';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';

import {
  AddFormComponent,
  FormField,
  TableSection,
  ActionButton,
} from '@shared/components/add-form/add-form.component';
import {    
  POR_ADD_FORM_FIELDS,
  POR_DETAIL_TABLE_SECTION,
  PurchaseReturnOrderDetailItem,
  PurchaseReturnOrderItem,
} from '../purchase-return-order.config';
import { PurchaseReturnOrderService } from '../purchase-return-order.service';
import { VendorOptionsService } from '@shared/services/vendor-options.service';
import { WarehouseOptionsService } from '@shared/services/warehouse-options.service';
import { 
  PORStockSelectionDialogComponent,
  PORStockSelectionItem 
} from '../purchase-return-order-add/components/por-stock-selection-dialog/por-stock-selection-dialog.component';

@Component({
  selector: 'app-purchase-return-order-edit',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AddFormComponent,
    ButtonModule,
    ToastModule,
    PORStockSelectionDialogComponent,
  ],
  providers: [MessageService],
  templateUrl: './purchase-return-order-edit.component.html',
  styleUrl: './purchase-return-order-edit.component.css',
})
export class PurchaseReturnOrderEditComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  @ViewChild('addForm') addFormComponent?: AddFormComponent;

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);
  private porService = inject(PurchaseReturnOrderService);
  private vendorOptionsService = inject(VendorOptionsService);
  private warehouseOptionsService = inject(WarehouseOptionsService);
  private cdr = inject(ChangeDetectorRef);
  private destroy$ = new Subject<void>();

  // Form configuration
  formFields = signal<FormField[]>(POR_ADD_FORM_FIELDS);
  headerActions = signal<ActionButton[]>([
    {
      label: 'common.actions.save',
      severity: 'info',
      action: 'save',
    },
    {
      label: 'common.actions.cancel',
      severity: 'secondary',
      action: 'cancel',
      outlined: true,
    },
  ]);

  // Data
  detailList = signal<PurchaseReturnOrderDetailItem[]>([]);
  loading = signal(false);
  currentFormData: any = null;
  private skipCanDeactivate = false;
  porId: number | null = null;
  porLoaded = false; // Flag to prevent multiple loads
  warehouseCode = signal<string>(''); // Warehouse code for stock lookup
  originalPoId: number | null = null; // Store poId (number) from POR for API
  private poOptionsLoaded = false; // Track if PO options have been loaded

  // Stock selection dialog (for adding items when no PO)
  showStockDialog = signal(false);
  dialogWarehouseCode = signal<string>('');
  existingSkuCodes = computed(() => {
    return this.detailList().map(item => item.skuCode);
  });

  // Options
  vendorSkuCodes = signal<string[]>([]);
  vendorSkuMap = signal<Map<string, { vendorSkuCode: string; vendorSkuName: string }>>(
    new Map()
  );

  vendorOptions = signal<any[]>([]);
  warehouseOptions = signal<any[]>([]);
  poOptions = signal<any[]>([]);

  // Selected values
  selectedVendorId = signal<number | null>(null);
  selectedWarehouseId = signal<number | null>(null);
  selectedPOId = signal<number | null>(null);

  // Computed table sections - disable all columns except returnQty and reason
  // Disable addItem button when PO is selected
  // Allow addItem when no PO (both edit and create mode)
  tableSections = computed(() => {
    const hasPO = !!this.selectedPOId() || !!this.originalPoId;
    const hasWarehouse = !!this.warehouseCode();
    const hasVendor = !!this.currentFormData?.vendorId;

    const actions = POR_DETAIL_TABLE_SECTION.actions?.map(action => {
      if (action.action === 'addItem') {
        return {
          ...action,
          disabled: hasPO || !hasWarehouse || !hasVendor,
        };
      }
      return action;
    });
    
    const section = {
      ...POR_DETAIL_TABLE_SECTION,
      data: this.detailList(),
      columns: POR_DETAIL_TABLE_SECTION.columns
        .map((col) => {
          if (col.prop === 'returnQty' || col.prop === 'reason') {
            return {
              ...col,
              disabled: false,
            };
          }
          return {
            ...col,
            disabled: true,
          };
        }) || [],
      actions: actions,
    };
    return [section];
  });

  private cellValueChange$ = new Subject<{
    id: string;
    field: string;
    value: any;
  }>();

  ngOnInit(): void {
    this.loadVendorOptions();
    this.loadWarehouseOptions();
    this.loadPOOptions();

    this.cellValueChange$
      .pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe((event) => {
        this.onCellValueChange(event);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadPOR(): void {
    if (!this.porId) {
      const id = this.route.snapshot.paramMap.get('id');
      if (!id) {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'purchase_return_order.messages.invalid_id'
          ),
        });
        this.router.navigate(['/procurement/purchase-return-order']);
        return;
      }
      this.porId = Number(id);
    }

    this.loading.set(true);

    this.porService
      .getPORDetailWithItems(this.porId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ por, items }) => {
          if (por.status !== 'PENDING') {
            this.messageService.add({
              severity: 'warn',
              summary: this.translateService.instant('common.messages.warning'),
              detail: this.translateService.instant(
                'purchase_return_order.messages.cannot_edit_non_draft'
              ),
            });
            this.router.navigate(['/procurement/purchase-return-order']);
            return;
          }
          this.populateForm(por);
          this.detailList.set(items);
          this.updateFormFieldsForEdit();

          // Load vendor SKUs to filter products when no PO (Edit always has por.vendorId from API)
          if (!this.originalPoId && por.vendorId != null) {
            this.loadVendorSkus(por.vendorId);
          }

          if (por.warehouseCode && items.length > 0) {
            this.loadStockForItems(por.warehouseCode, items);
          } else {
            this.loading.set(false);
          }
        },
        error: (error) => {
          console.error('Error loading POR:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'purchase_return_order.messages.load_error'
            ),
          });
          this.loading.set(false);
          this.router.navigate(['/procurement/purchase-return-order']);
        },
      });
  }

  private populateForm(por: PurchaseReturnOrderItem): void {
    let vendor = this.vendorOptions().find((v) => v.code === por.vendorCode);
    if (!vendor && por.vendorId != null) {
      vendor = this.vendorOptions().find((v) => v.value === por.vendorId);
    }
    const vendorCode = por.vendorCode || vendor?.code || '';
    const vendorName = por.vendorName || vendor?.name || '';

    const warehouse = this.warehouseOptions().find(
      (w) => w.code === por.warehouseCode
    );

    let poId = por.poId || null;
    if (!poId && por.poCode) {
      const po = this.poOptions().find((p) => p.poNo === por.poCode);
      if (po) {
        poId = po.id;
      }
    }
    this.originalPoId = poId;
    this.currentFormData = {
      porNo: por.porNo || por.rtvNo || '',
      poId: por.poCode || '',
      poCode: por.poCode || '',
      vendorId: vendor?.value ?? por.vendorId ?? null,
      vendorCode,
      vendorName,
      warehouseId: warehouse?.value || null,
      warehouseCode: por.warehouseCode,
      warehouseName: por.warehouseName,
      reason: por.reason || '',
      returnDate: por.returnDate || null,
    };

    // Store warehouse code for stock lookup
    this.warehouseCode.set(por.warehouseCode);
  }

  private loadVendorSkus(vendorId: number): void {
    this.vendorSkuMap.set(new Map());
    this.vendorSkuCodes.set([]);
    this.porService
      .loadVendorSkuList(vendorId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (list) => {
          const { map, skuCodes } =
            this.porService.buildVendorSkuMapAndCodes(list);
          this.vendorSkuMap.set(map);
          this.vendorSkuCodes.set(skuCodes);
        },
        error: () => {
          this.vendorSkuCodes.set([]);
        },
      });
  }

  // Load stock for items
  private loadStockForItems(
    warehouseCode: string,
    items: PurchaseReturnOrderDetailItem[]
  ): void {
    const skuCodes = items
      .map((item) => item.skuCode)
      .filter((code) => !!code);
    
    if (skuCodes.length === 0) {
      this.loading.set(false);
      return;
    }

    this.porService
      .loadStockBySkuCodes(warehouseCode, skuCodes)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ items: stockItems }) => {
          // Create a map of skuCode -> availableQuantity for quick lookup
          const stockMap = new Map<string, number>();
          // Create a map of skuCode -> skuName for quick lookup
          const skuNameMap = new Map<string, string>();
          stockItems.forEach((stockItem: any) => {
            if (stockItem.skuCode) {
              // availableQuantity is computed by IMS in
              // PurchaseReturnOrderService.loadStockBySkuCodes() - not re-derived here.
              stockMap.set(stockItem.skuCode, stockItem.availableQuantity ?? 0);
              // Map skuName from stock API if available
              if (stockItem.skuName) {
                skuNameMap.set(stockItem.skuCode, stockItem.skuName);
              }
            }
          });

          // Update detailList with available quantities and skuName
          // Preserve tax field when updating
          const updatedItems = this.detailList().map((item) => {
            const availableQuantity =
              stockMap.get(item.skuCode) ?? item.qtyAvailable;
            const skuName = skuNameMap.get(item.skuCode) ?? item.skuName;
            return {
              ...item,
              qtyAvailable: availableQuantity,
              currentStock: availableQuantity,
              skuName: skuName, // Update skuName from stock API
              // Preserve tax field (rate from API)
              tax: item.tax,
            };
          });

          this.detailList.set(updatedItems);
          this.loading.set(false);
        },
        error: (error) => {
          console.error('Error loading stock:', error);
          this.loading.set(false);
        },
      });
  }

  // Get field error for validation
  getFieldError = (rowIndex: number, fieldName: string): string | null => {
    if (fieldName !== 'returnQty') {
      return null;
    }

    // Don't validate if data is still loading to avoid flashing red border
    if (this.loading()) {
      return null;
    }

    const items = this.detailList();
    if (rowIndex < 0 || rowIndex >= items.length) {
      return null;
    }

    const item = items[rowIndex];
    
    // Don't validate if qtyAvailable is not yet loaded (undefined/null)
    // This prevents red border flash when data is still being loaded
    if (item.qtyAvailable === undefined || item.qtyAvailable === null) {
      return null;
    }

    const returnQty = item.returnQty || 0;
    const availableQty = item.qtyAvailable || 0;

    // Validate: returnQty must be > 0 and <= availableQty
    if (returnQty <= 0) {
      return this.translateService.instant(
        'purchase_return_order.edit.validation.quantity_required'
      );
    }

    if (returnQty > availableQty) {
      return this.translateService.instant(
        'purchase_return_order.edit.validation.quantity_exceeds_stock',
        {
          returnQty: returnQty,
          available: availableQty,
        }
      );
    }

    return null;
  };

  // Validate before saving - check all items for validation errors
  private validateBeforeSave(): string[] {
    const errors: string[] = [];
    const items = this.detailList();

    items.forEach((item, index) => {
      const returnQty = item.returnQty || 0;
      const availableQty = item.qtyAvailable || 0;

      // Check if quantity is valid
      if (returnQty <= 0) {
        errors.push(
          this.translateService.instant(
            'purchase_return_order.edit.validation.quantity_required'
          ) || `Mặt hàng "${item.skuName || item.skuCode}": Số lượng điều chuyển phải lớn hơn 0`
        );
      } else if (returnQty > availableQty) {
        errors.push(
          this.translateService.instant(
            'purchase_return_order.edit.validation.quantity_exceeds_stock',
            {
              returnQty: returnQty,
              available: availableQty,
            }
          ) || `Mặt hàng "${item.skuName || item.skuCode}": Số lượng vượt quá tồn kho khả dụng (${availableQty})`
        );
      }
    });

    return errors;
  }

  // Update form fields for edit mode - disable all except reason
  private updateFormFieldsForEdit(): void {
    const updatedFields: FormField[] = this.formFields().map((field): FormField => {
      // Only reason field is editable, all others are disabled
      if (field.controlName === 'reason') {
        return {
          ...field,
          disabled: false,
        };
      }
      
      // Convert poId dropdown to text in edit mode to display PO number
      // Map poId field to display poNo (string) instead of poId (number)
      if (field.controlName === 'poId') {
        return {
          ...field,
          type: 'text' as const,
          disabled: true,
          // Map controlName to poNo for display, but keep poId in formData
        };
      }
      
      return {
        ...field,
        disabled: true,
      };
    });
    this.formFields.set(updatedFields);
  }

  // Load vendor options
  private loadVendorOptions(): void {
    this.loadAllVendorOptions(1, [])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (
          options: Array<{
            label: string;
            value: number;
            code: string;
            name: string;
          }>,
        ) => {
          this.vendorOptions.set(options);
          this.updateFormFieldsWithOptions();
          this.checkAndLoadPOR();
        },
        error: (error: unknown) => {
          console.error('Error loading vendor options:', error);
        },
      });
  }

  // Load warehouse options
  private loadWarehouseOptions(): void {
    this.loadAllWarehouseOptions(1, [])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (
          warehouses: Array<{
            label: string;
            value: number;
            code: string;
            name: string;
          }>,
        ) => {
          this.warehouseOptions.set(warehouses);
          this.updateFormFieldsWithOptions();
          this.checkAndLoadPOR();
        },
        error: (error: unknown) => {
          console.error('Error loading warehouse options:', error);
        },
      });
  }

  private loadAllVendorOptions(
    page: number,
    acc: Array<{ label: string; value: number; code: string; name: string }>,
  ): Observable<Array<{ label: string; value: number; code: string; name: string }>> {
    return this.vendorOptionsService
      .getOptionsPage({ page, size: 100, searchText: '' }, { valueKey: 'id' })
      .pipe(
        switchMap((res) => {
          const merged = [
            ...acc,
            ...res.options.map((opt) => {
              const vendor = this.vendorOptionsService.getVendorById(
                Number(opt.value),
              );
              return {
                label: opt.label,
                value: Number(opt.value),
                code: vendor?.code || '',
                name: vendor?.name || opt.label,
              };
            }),
          ];
          const hasMore =
            typeof res.totalElements === 'number'
              ? merged.length < res.totalElements
              : res.pageContentLength === 100;
          if (!hasMore || res.pageContentLength === 0) {
            return of(merged);
          }
          return this.loadAllVendorOptions(page + 1, merged);
        }),
      );
  }

  private loadAllWarehouseOptions(
    page: number,
    acc: Array<{ label: string; value: number; code: string; name: string }>,
  ): Observable<Array<{ label: string; value: number; code: string; name: string }>> {
    return this.warehouseOptionsService
      .getOptionsPage(
        { page, size: 100, searchText: '' },
        { valueKey: 'id', status: true },
      )
      .pipe(
        switchMap((res) => {
          const merged = [
            ...acc,
            ...res.options.map((opt) => ({
              label: opt.label,
              value: Number(opt.value),
              code: String(opt.value),
              name: opt.label,
            })),
          ];
          const hasMore =
            typeof res.totalElements === 'number'
              ? merged.length < res.totalElements
              : res.pageContentLength === 100;
          if (!hasMore || res.pageContentLength === 0) {
            return of(merged);
          }
          return this.loadAllWarehouseOptions(page + 1, merged);
        }),
      );
  }

  // Load PO options
  private loadPOOptions(): void {
    this.porService
      .getAvailablePOs()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (pos) => {
          this.poOptions.set(pos);
          this.poOptionsLoaded = true;
          // After PO options are loaded, check if we can load POR
          this.checkAndLoadPOR();
        },
        error: (error) => {
          console.error('Error loading PO options:', error);
          this.poOptionsLoaded = true; // Mark as loaded even on error
          // Still check if we can load POR even if PO options fail
          this.checkAndLoadPOR();
        },
      });
  }

  // Check if all options are loaded, then load POR
  private checkAndLoadPOR(): void {
    if (
      !this.porLoaded &&
      this.vendorOptions().length > 0 &&
      this.warehouseOptions().length > 0 &&
      this.poOptionsLoaded
    ) {
      const id = this.route.snapshot.paramMap.get('id');
      if (id) {
        this.porId = Number(id);
        this.porLoaded = true;
        this.loadPOR();
      }
    }
  }

  // Update form fields with options
  private updateFormFieldsWithOptions(): void {
    const updatedFormFields = this.formFields().map((field) => {
      if (field.controlName === 'vendorId') {
        return {
          ...field,
          options: this.vendorOptions().map((opt) => ({
            label: opt.label,
            value: opt.value,
          })),
        };
      }
      if (field.controlName === 'warehouseId') {
        return {
          ...field,
          options: this.warehouseOptions().map((opt) => ({
            label: opt.label,
            value: opt.value,
          })),
        };
      }
      if (field.controlName === 'poId') {
        return {
          ...field,
          options: this.poOptions().map((po) => ({
            label: `${po.poNo} - ${po.vendorName}`,
            value: po.id,
          })),
        };
      }
      return field;
    });
    this.formFields.set(updatedFormFields);
  }

  // Handle cell value change
  private onCellValueChange(event: {
    id: string;
    field: string;
    value: any;
  }): void {
    if (event.field === 'returnQty') {
      const item = this.detailList().find((item) => item.id === event.id);
      if (item) {
        // Preserve user input value - don't reset even if invalid
        // Convert to number but keep original if conversion fails
        let returnQty: number;
        if (event.value === null || event.value === undefined || event.value === '') {
          returnQty = 0;
        } else {
          const parsed = Number(event.value);
          returnQty = isNaN(parsed) ? (item.returnQty || 0) : parsed;
        }
        
        const returnAmount = returnQty * (item.price || 0);
        const updatedList = this.detailList().map((i) =>
          i.id === event.id
            ? { ...i, returnQty: returnQty, returnAmount: returnAmount, tax: i.tax } // Preserve tax
            : i
        );
        this.detailList.set(updatedList);
        // Trigger change detection to update validation
        this.cdr.detectChanges();
      }
    } else if (event.field === 'reason') {
      const updatedList = this.detailList().map((i) =>
        i.id === event.id ? { ...i, reason: event.value || '', tax: i.tax } : i // Preserve tax
      );
      this.detailList.set(updatedList);
    }
  }

  onFormValueChange(formValue: any): void {
    if (formValue) {
      const preservedPoCode = this.currentFormData?.poCode;
      this.currentFormData = {
        ...this.currentFormData,
        ...formValue,
        poId: preservedPoCode,
      };
    }
  }

  // Handle form actions
  onFormAction(action: string): void {
    switch (action) {
      case 'save':
        this.onSave();
        break;
      case 'cancel':
        this.onCancel();
        break;
    }
  }

  // Handle table actions
  onTableAction(event: { action: string; data?: any }): void {
    switch (event.action) {
      case 'cellValueChange':
        // Update detailList immediately for validation
        if (event.data?.field === 'returnQty' || event.data?.field === 'reason') {
          this.onCellValueChange(event.data);
        } else {
          this.cellValueChange$.next(event.data);
        }
        break;
      case 'cellInputChange':
        // Handle real-time input changes for validation
        // Preserve user input value - don't reset even if invalid
        if (event.data?.prop === 'returnQty') {
          const rowData = event.data.rowData;
          const item = this.detailList().find((item) => item.id === rowData.id);
          if (item) {
            // Preserve user input value - don't reset even if invalid
            // Only update if value is valid number, otherwise keep current value
            let returnQty: number;
            const inputValue = event.data.value;
            if (inputValue === null || inputValue === undefined || inputValue === '') {
              returnQty = 0;
            } else {
              const parsed = Number(inputValue);
              // If parsing fails, keep the current value to preserve user input
              returnQty = isNaN(parsed) ? (item.returnQty || 0) : parsed;
            }
            
            const updatedList = this.detailList().map((i) =>
              i.id === rowData.id ? { ...i, returnQty: returnQty, tax: i.tax } : i // Preserve tax
            );
            this.detailList.set(updatedList);
            // Trigger change detection to update validation immediately
            this.cdr.detectChanges();
          }
        }
        break;
      case 'addItem':
        this.onAddItem();
        break;
    }
  }

  // Add item from stock selection dialog (when no PO)
  private onAddItem(): void {
    const warehouseCode = this.warehouseCode();
    if (!warehouseCode) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'purchase_return_order.messages.warehouse_required'
        ),
      });
      return;
    }
    if (!this.currentFormData?.vendorId) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'purchase_return_order.messages.vendor_required'
        ),
      });
      return;
    }
    this.dialogWarehouseCode.set(warehouseCode);
    this.showStockDialog.set(true);
  }

  // Handle stock selection dialog confirm
  onStockDialogConfirm(selectedItems: PORStockSelectionItem[]): void {
    if (!selectedItems || selectedItems.length === 0) {
      return;
    }

    const currentReason = this.currentFormData?.reason || '';
    const vMap = this.vendorSkuMap();

    const newItems: PurchaseReturnOrderDetailItem[] = selectedItems
      .map((item, index) => {
        const existingItem = this.detailList().find(
          (i) => i.skuCode === item.skuCode
        );
        if (existingItem) {
          return null;
        }
        const v = vMap.get(item.skuCode);
        return {
          id: `item-${Date.now()}-${index}`,
          grnDetailId: 0,
          skuCode: item.skuCode || '',
          skuName: item.skuName || '',
          vendorSkuCode: v?.vendorSkuCode ?? (item as any).vendorSkuCode ?? '',
          vendorSkuName: v?.vendorSkuName ?? (item as any).vendorSkuName ?? '',
        spuCode: item.spuCode || '',
        spuName: item.spuName || '',
        uom: '', // TODO: Get from SKU
        qtyReceived: 0, // Not applicable when manually added
        qtyReturnedBefore: 0,
        qtyAvailable: item.availableQuantity || 0,
        currentStock: item.stockNum || 0,
        returnQty: 1, // Default to 1
        price: 0, // User can input manually
        returnAmount: 0,
        taxAmount: 0,
        reason: currentReason,
      } as PurchaseReturnOrderDetailItem;
      })
      .filter((item): item is PurchaseReturnOrderDetailItem => item !== null);

    // Add new items to detail list
    if (newItems.length > 0) {
      const updatedList = [...this.detailList(), ...newItems];
      this.detailList.set(updatedList);
    }
  }

  // Handle stock dialog visible change
  onStockDialogVisibleChange(visible: boolean): void {
    this.showStockDialog.set(visible);
  }

  // Save
  private onSave(): void {
    if (!this.porId) {
      return;
    }
    
    // Validate before saving - check for validation errors
    const validationErrors = this.validateBeforeSave();
    if (validationErrors.length > 0) {
      // Show validation errors
      validationErrors.forEach(error => {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: error,
        });
      });
      return;
    }
    
    const payload = {
      warehouseCode: this.currentFormData?.warehouseCode || '',
      vendorCode: this.currentFormData?.vendorCode || '',
      reason: this.currentFormData?.reason || '',
      poId: this.originalPoId || this.currentFormData?.poId, // poId (number) from original POR - same as warehouseCode/vendorCode
      items: this.detailList()
        .filter((item) => {
          // Filter out items with invalid price or quantity
          const price = Number(item.price || 0);
          const returnQty = Number(item.returnQty || 0);
          return price > 0 && returnQty > 0;
        })
        .map((item) => {
          const price = Number(item.price || 0);
          const returnQty = Number(item.returnQty || 0);
          
          // Get tax - tax is rate from API (0.08), displayed as percentage (8) in UI via taxAmount
          // When saving, use tax directly (already rate format from API)
          // taxAmount is percentage for display (8), tax is rate (0.08) from API
          let tax = 0;
          if (item.tax !== undefined && item.tax !== null) {
            tax = item.tax; // Use tax rate directly from API
          } else if (item.taxAmount !== undefined && item.taxAmount !== null && item.taxAmount !== 0) {
            // Fallback: convert taxAmount (percentage) to rate
            tax = item.taxAmount / 100;
          }
          
          return {
            skuCode: item.skuCode || '',
            vendorSkuCode: item.vendorSkuCode ?? '',
            vendorSkuName: item.vendorSkuName ?? '',
            price: price > 0 ? price : 0.01, // Ensure positive number
            plannedQty: returnQty > 0 ? returnQty : 1, // Ensure positive number
            tax: tax, // Use tax rate from API
            reason: item.reason || '',
          };
        }),
    };

    this.loading.set(true);
    this.porService
      .updatePOR(this.porId, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.loading.set(false);
          this.skipCanDeactivate = true;
          this.addFormComponent?.form?.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'purchase_return_order.messages.update_success'
            ),
          });
          this.router.navigate(['/procurement/purchase-return-order']);
        },
        error: (error) => {
          console.error('Error updating POR:', error);
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'purchase_return_order.messages.update_error'
            ),
          });
        },
      });
  }

  // Cancel
  private onCancel(): void {
    this.router.navigate(['/procurement/purchase-return-order']);
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate || this.loading()) {
      return true;
    }

    return !(this.addFormComponent?.form?.dirty ?? false);
  }
}

